import React, { useEffect, useState, useMemo } from "react";
import DashboardLayout from "../DashboardLayout/DasboardLayout";
import { API_URL } from "../config";
import { 
  TrendingUp, 
  TrendingDown, 
  Users, 
  UserCheck, 
  DollarSign, 
  Activity,
  Filter,
  Download,
  RefreshCw,
  ChevronUp,
  ChevronDown,
  Calendar,
  AlertCircle,
  Car
} from "lucide-react";

// Collapsible Tooltip Component
const ReportTooltip = ({ title, children }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-blue-100 border-2 border-blue-300 rounded-lg p-6 mb-6 shadow-lg">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left hover:bg-blue-200 p-2 rounded transition-colors"
      >
        <div className="flex items-center space-x-2">
          <AlertCircle className="text-blue-600" size={24} />
          <h3 className="text-xl font-bold text-blue-900">{title}</h3>
        </div>
        {isExpanded ? <ChevronUp className="text-blue-600" size={24} /> : <ChevronDown className="text-blue-600" size={24} />}
      </button>
      {isExpanded && (
        <div className="mt-4 text-sm text-blue-900 space-y-3 bg-white p-4 rounded border border-blue-200">
          {children}
        </div>
      )}
    </div>
  );
};

const PerformanceReports = () => {
  const [drivers, setDrivers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [trips, setTrips] = useState([]);
  const [rideshareRequests, setRideshareRequests] = useState([]);
  const [rideshareRoutes, setRideshareRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({});
  const [timeFilter, setTimeFilter] = useState("all"); // all, today, 7days, 14days, 30days

  // Get date range based on filter
  const getDateRange = (filter) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (filter) {
      case "today":
        return {
          start: today,
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      case "7days":
        return {
          start: new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000),
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      case "14days":
        return {
          start: new Date(today.getTime() - 13 * 24 * 60 * 60 * 1000),
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      case "30days":
        return {
          start: new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000),
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      default:
        return null; // all time
    }
  };

  // Filter trips based on date range
  const filterTripsByDate = (trips, dateRange) => {
    if (!dateRange) return trips;
    
    return trips.filter(trip => {
      if (!trip.order_start_datetime) return false;
      const tripDate = new Date(trip.order_start_datetime);
      return tripDate >= dateRange.start && tripDate <= dateRange.end;
    });
  };

  // Fetch all data
  const fetchData = async () => {
    setLoading(true);
    try {
      const [driversRes, customersRes, tripsRes, rideshareRequestsRes, rideshareRoutesRes] = await Promise.all([
        fetch(`${API_URL}/driver/`),
        fetch(`${API_URL}/customerdetails/`),
        fetch(`${API_URL}/trip/`),
        fetch(`${API_URL}/rideshare/requests/all`),
        fetch(`${API_URL}/rideshare/trips`)
      ]);

      const [driversData, customersData, tripsData, rideshareRequestsData, rideshareRoutesData] = await Promise.all([
        driversRes.json(),
        customersRes.json(),
        tripsRes.json(),
        rideshareRequestsRes.json(),
        rideshareRoutesRes.json()
      ]);

      setDrivers(driversData || []);
      setCustomers(customersData || []);
      setTrips(tripsData || []);
      setRideshareRequests(Array.isArray(rideshareRequestsData) ? rideshareRequestsData : []);
      setRideshareRoutes(Array.isArray(rideshareRoutesData) ? rideshareRoutesData : []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Calculate performance metrics
  const performanceData = useMemo(() => {
    const dateRange = getDateRange(timeFilter);
    const filteredTrips = filterTripsByDate(trips, dateRange);

    // `/trip` is exclusively the private-ride/delivery table — every row here is a private
    // trip, there's no `trip_type`/`rideshare_id` field on these rows to filter by.
    const privateTrips = filteredTrips;

    // Rideshare revenue lives on accepted `rideshare_requests` rows (one per passenger,
    // `accepted_amount` is the fare-of-record), not on `/trip`. Join each request back to its
    // route (`rideshare_trips`) to get the driver who gets credit for that fare.
    const driverIdByRideshareId = {};
    rideshareRoutes.forEach(route => {
      driverIdByRideshareId[route.rideshare_id] = route.driver_id;
    });

    const filteredRideshareRequests = (dateRange
      ? rideshareRequests.filter(req => {
          if (!req.created_at) return false;
          const d = new Date(req.created_at);
          return d >= dateRange.start && d <= dateRange.end;
        })
      : rideshareRequests
    ).filter(req => (parseFloat(req.accepted_amount) || parseFloat(req.offer_amount) || 0) > 0);

    const calculateMetrics = (data, type, tripList, rideshareList = []) => {
      return data.map(item => {
        // Handle different ID field names for customers vs drivers
        const itemId = type === 'driver' ? item.driver_id : item.customerid; // FIXED: Use customerid instead of cust_id
        const relatedTrips = tripList.filter(trip =>
          type === 'driver' ? trip.driver_id == itemId : trip.cust_id == itemId // FIXED: Match trip.cust_id with customer.customerid
        );
        const relatedRideshareRequests = rideshareList.filter(req =>
          type === 'driver' ? driverIdByRideshareId[req.rideshare_id] == itemId : req.passenger_id == itemId
        );

        const privateValue = relatedTrips.reduce((sum, trip) =>
          sum + (parseFloat(trip.delivery_cost_proposed) || 0), 0
        );
        const rideshareValue = relatedRideshareRequests.reduce((sum, req) =>
          sum + (parseFloat(req.accepted_amount) || parseFloat(req.offer_amount) || 0), 0
        );

        const tripCount = relatedTrips.length + relatedRideshareRequests.length;
        const totalValue = privateValue + rideshareValue;
        const avgValue = tripCount > 0 ? totalValue / tripCount : 0;

        return {
          ...item,
          tripCount,
          totalValue,
          avgValue,
          relatedTrips,
          relatedRideshareRequests
        };
      });
    };

    // Calculate metrics for all trips (private + rideshare combined)
    const driversWithMetrics = calculateMetrics(drivers, 'driver', filteredTrips, filteredRideshareRequests);
    const customersWithMetrics = calculateMetrics(customers, 'customer', filteredTrips, filteredRideshareRequests);

    // Calculate metrics for private trips only
    const driversPrivateMetrics = calculateMetrics(drivers, 'driver', privateTrips);
    const customersPrivateMetrics = calculateMetrics(customers, 'customer', privateTrips);

    // Calculate metrics for rideshare requests only
    const driversRideshareMetrics = calculateMetrics(drivers, 'driver', [], filteredRideshareRequests);
    const customersRideshareMetrics = calculateMetrics(customers, 'customer', [], filteredRideshareRequests);

    return {
      drivers: driversWithMetrics,
      customers: customersWithMetrics,
      driversPrivate: driversPrivateMetrics,
      customersPrivate: customersPrivateMetrics,
      driversRideshare: driversRideshareMetrics,
      customersRideshare: customersRideshareMetrics,
      filteredTrips,
      privateTrips,
      rideshareTrips: filteredRideshareRequests,
      totalCount: filteredTrips.length + filteredRideshareRequests.length
    };
  }, [drivers, customers, trips, rideshareRequests, rideshareRoutes, timeFilter]);

  // Get top/bottom performers
  const getPerformers = (data, metric, order = 'desc') => {
    return [...data]
      .filter(item => item[metric] > 0)
      .sort((a, b) => order === 'desc' ? b[metric] - a[metric] : a[metric] - b[metric])
      .slice(0, 20);
  };

  // Get inactive performers (zero values)
  const getInactivePerformers = (data, metric) => {
    return [...data]
      .filter(item => item[metric] === 0)
      .sort((a, b) => {
        // Sort by name for consistent ordering
        const nameA = (a.name || `${a.first_name || ''} ${a.last_name || ''}`).trim().toLowerCase();
        const nameB = (b.name || `${b.first_name || ''} ${b.last_name || ''}`).trim().toLowerCase();
        return nameA.localeCompare(nameB);
      });
  };

  const topDriversByCount = getPerformers(performanceData.drivers, 'tripCount', 'desc');
  const topCustomersByCount = getPerformers(performanceData.customers, 'tripCount', 'desc');
  const topDriversByValue = getPerformers(performanceData.drivers, 'totalValue', 'desc');
  const topCustomersByValue = getPerformers(performanceData.customers, 'totalValue', 'desc');
  
  const bottomDriversByCount = getPerformers(performanceData.drivers, 'tripCount', 'asc');
  const bottomCustomersByCount = getPerformers(performanceData.customers, 'tripCount', 'asc');
  const bottomDriversByValue = getPerformers(performanceData.drivers, 'totalValue', 'asc');
  const bottomCustomersByValue = getPerformers(performanceData.customers, 'totalValue', 'asc');

  // Private trips performers
  const topPrivateDriversByCount = getPerformers(performanceData.driversPrivate, 'tripCount', 'desc');
  const topPrivateCustomersByCount = getPerformers(performanceData.customersPrivate, 'tripCount', 'desc');
  const topPrivateDriversByValue = getPerformers(performanceData.driversPrivate, 'totalValue', 'desc');
  const topPrivateCustomersByValue = getPerformers(performanceData.customersPrivate, 'totalValue', 'desc');

  // Rideshare trips performers
  const topRideshareDriversByCount = getPerformers(performanceData.driversRideshare, 'tripCount', 'desc');
  const topRideshareCustomersByCount = getPerformers(performanceData.customersRideshare, 'tripCount', 'desc');
  const topRideshareDriversByValue = getPerformers(performanceData.driversRideshare, 'totalValue', 'desc');
  const topRideshareCustomersByValue = getPerformers(performanceData.customersRideshare, 'totalValue', 'desc');

  // Inactive performers (zero values)
  const inactiveDriversByCount = getInactivePerformers(performanceData.drivers, 'tripCount');
  const inactiveCustomersByCount = getInactivePerformers(performanceData.customers, 'tripCount');
  const inactiveDriversByValue = getInactivePerformers(performanceData.drivers, 'totalValue');
  const inactiveCustomersByValue = getInactivePerformers(performanceData.customers, 'totalValue');

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Get filter label
  const getFilterLabel = (filter) => {
    switch(filter) {
      case "today": return "Today";
      case "7days": return "Past 7 Days";
      case "14days": return "Past 14 Days";
      case "30days": return "Past 30 Days";
      default: return "All Time";
    }
  };

  const exportData = (data, filename) => {
    const csv = [
      ['Rank', 'ID', 'Name', 'Email', 'Phone', 'Trip Count', 'Total Value', 'Average Value'],
      ...data.map((item, index) => [
        index + 1,
        item.driver_id || item.customerid,
        item.name ? `${item.name} ${item.surname || ''}`.trim() : (item.first_name || item.last_name ? `${item.first_name || ''} ${item.last_name || ''}`.trim() : 'N/A'),
        item.email,
        item.phone,
        item.tripCount,
        item.totalValue.toFixed(2),
        item.avgValue.toFixed(2)
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const PerformanceTable = ({ title, data, icon: Icon, type, metric, isHighest = true }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden mb-6">
      <div 
        className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white cursor-pointer"
        onClick={() => toggleSection(title)}
      >
        <div className="flex items-center space-x-3">
          <Icon size={20} />
          <h3 className="text-lg font-semibold">{title}</h3>
          <span className="bg-white/20 px-2 py-1 rounded text-sm">
            {data.length} records
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              exportData(data, `${title.replace(/\s+/g, '_')}.csv`);
            }}
            className="p-2 hover:bg-white/20 rounded transition-colors"
          >
            <Download size={16} />
          </button>
          {expandedSections[title] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </div>
      
      {expandedSections[title] && (
        <div className="p-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-300">
                <tr>
                  <th className="px-4 py-3">Rank</th>
                  <th className="px-4 py-3">ID</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Trips</th>
                  <th className="px-4 py-3">Total Value</th>
                  <th className="px-4 py-3">Avg Value</th>
                </tr>
              </thead>
              <tbody>
                {data.map((item, index) => (
                  <tr key={item.driver_id || item.customerid} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3 font-medium">
                      <div className="flex items-center space-x-2">
                        <span className="text-lg font-bold text-gray-500">#{index + 1}</span>
                        {index < 3 && (
                          <span className="text-yellow-500">🏆</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {item.driver_id || item.customerid}
                    </td>
                    <td className="px-4 py-3 font-medium">
                      {item.name ? `${item.name} ${item.surname || ''}`.trim() : (item.first_name || item.last_name ? `${item.first_name || ''} ${item.last_name || ''}`.trim() : 'N/A')}
                    </td>
                    <td className="px-4 py-3 text-xs">{item.email || 'N/A'}</td>
                    <td className="px-4 py-3 text-xs">{item.phone || 'N/A'}</td>
                    <td className="px-4 py-3">
                      <span className="font-semibold text-blue-600">
                        {item.tripCount}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-semibold text-green-600">
                        ${item.totalValue.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      ${item.avgValue.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <RefreshCw className="animate-spin text-blue-500" size={40} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Performance Reports Dashboard
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Comprehensive performance analytics for drivers and customers
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Calendar className="text-gray-500" size={20} />
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              {getFilterLabel(timeFilter)}
            </span>
          </div>
        </div>

        {/* Time Filter Buttons */}
        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { value: "all", label: "All Time" },
            { value: "today", label: "Today" },
            { value: "7days", label: "Past 7 Days" },
            { value: "14days", label: "Past 14 Days" },
            { value: "30days", label: "Past 30 Days" }
          ].map(filter => (
            <button
              key={filter.value}
              onClick={() => setTimeFilter(filter.value)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                timeFilter === filter.value
                  ? "bg-blue-600 text-white shadow-lg transform scale-105"
                  : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Report Tooltip */}
      <ReportTooltip title="Performance Reports - Business Value & Features">
        <div className="space-y-3">
          <div>
            <strong className="text-blue-900">📊 Report Overview:</strong>
            <p>Comprehensive performance analytics for drivers and customers with trip counts, revenue metrics, and service type breakdowns.</p>
          </div>
          
          <div>
            <strong className="text-blue-900">🎯 Key Business Questions Answered:</strong>
            <ul className="list-disc ml-4 space-y-1">
              <li><strong>Driver Management:</strong> Who are my top performers? Which drivers need training? Who generates most revenue?</li>
              <li><strong>Customer Analysis:</strong> Who are my most valuable customers? Which customers need attention? Customer lifetime value?</li>
              <li><strong>Service Performance:</strong> How do private vs rideshare services compare? Which service is more profitable?</li>
              <li><strong>Operational Insights:</strong> What are the performance trends? Who should I reward or promote?</li>
            </ul>
          </div>
          
          <div>
            <strong className="text-blue-900">✅ Key Features:</strong>
            <ul className="list-disc ml-4 space-y-1">
              <li><strong>Performance Metrics:</strong> Trip counts, revenue, average values, service type breakdowns</li>
              <li><strong>Smart Rankings:</strong> Top 20 drivers and customers by multiple performance indicators</li>
              <li><strong>Service Analysis:</strong> Separate Private and Rideshare performance metrics</li>
              <li><strong>Real-Time Data:</strong> Live API calls with 30-second refresh intervals</li>
              <li><strong>Export Functionality:</strong> CSV download for detailed analysis and reporting</li>
              <li><strong>Time Filtering:</strong> Analyze performance by today, 7, 14, 30 days, or all time</li>
            </ul>
          </div>
          
          <div>
            <strong className="text-blue-900">💼 Business Impact:</strong>
            <p>This report provides immediate operational value by helping you identify top performers, optimize resource allocation, improve customer retention, and make data-driven decisions for driver incentives and customer rewards programs.</p>
          </div>
          
          <div>
            <strong className="text-blue-900">🔍 Data Source:</strong>
            <p>Uses REAL data from live API calls to trips, drivers, and customers endpoints. All metrics are calculated from actual completed trips with real revenue data.</p>
          </div>
        </div>
      </ReportTooltip>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Drivers</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{drivers.length}</p>
            </div>
            <UserCheck className="text-blue-500" size={24} />
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Customers</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{customers.length}</p>
            </div>
            <Users className="text-green-500" size={24} />
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Trips {timeFilter !== "all" ? `(${getFilterLabel(timeFilter)})` : "(All Time)"}
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {performanceData.totalCount || 0}
              </p>
            </div>
            <Activity className="text-purple-500" size={24} />
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Private Trips</p>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {performanceData.privateTrips?.length || 0}
              </p>
            </div>
            <Car className="text-blue-500" size={24} />
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Rideshare Trips</p>
              <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {performanceData.rideshareTrips?.length || 0}
              </p>
            </div>
            <Users className="text-orange-500" size={24} />
          </div>
        </div>
      </div>

      {/* Report Sections */}
      <div className="space-y-6">
        {/* Highest Performers */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <TrendingUp className="mr-2 text-green-500" />
            Highest Performers
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <PerformanceTable
              title="Top 20 Drivers by Trip Count"
              data={topDriversByCount}
              icon={UserCheck}
              type="driver"
              metric="tripCount"
              isHighest={true}
            />
            <PerformanceTable
              title="Top 20 Customers by Trip Count"
              data={topCustomersByCount}
              icon={Users}
              type="customer"
              metric="tripCount"
              isHighest={true}
            />
            <PerformanceTable
              title="Top 20 Drivers by Trip Value"
              data={topDriversByValue}
              icon={UserCheck}
              type="driver"
              metric="totalValue"
              isHighest={true}
            />
            <PerformanceTable
              title="Top 20 Customers by Trip Value"
              data={topCustomersByValue}
              icon={Users}
              type="customer"
              metric="totalValue"
              isHighest={true}
            />
          </div>
        </div>

        {/* Private Rides Performance */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <Car className="mr-2 text-blue-500" />
            Private Rides Performance
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <PerformanceTable
              title="Top 20 Drivers by Private Trip Count"
              data={topPrivateDriversByCount}
              icon={UserCheck}
              type="driver"
              metric="tripCount"
              isHighest={true}
            />
            <PerformanceTable
              title="Top 20 Customers by Private Trip Count"
              data={topPrivateCustomersByCount}
              icon={Users}
              type="customer"
              metric="tripCount"
              isHighest={true}
            />
            <PerformanceTable
              title="Top 20 Drivers by Private Trip Value"
              data={topPrivateDriversByValue}
              icon={UserCheck}
              type="driver"
              metric="totalValue"
              isHighest={true}
            />
            <PerformanceTable
              title="Top 20 Customers by Private Trip Value"
              data={topPrivateCustomersByValue}
              icon={Users}
              type="customer"
              metric="totalValue"
              isHighest={true}
            />
          </div>
        </div>

        {/* Rideshare Performance */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <Users className="mr-2 text-orange-500" />
            Rideshare Performance
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <PerformanceTable
              title="Top 20 Drivers by Rideshare Count"
              data={topRideshareDriversByCount}
              icon={UserCheck}
              type="driver"
              metric="tripCount"
              isHighest={true}
            />
            <PerformanceTable
              title="Top 20 Customers by Rideshare Count"
              data={topRideshareCustomersByCount}
              icon={Users}
              type="customer"
              metric="tripCount"
              isHighest={true}
            />
            <PerformanceTable
              title="Top 20 Drivers by Rideshare Value"
              data={topRideshareDriversByValue}
              icon={UserCheck}
              type="driver"
              metric="totalValue"
              isHighest={true}
            />
            <PerformanceTable
              title="Top 20 Customers by Rideshare Value"
              data={topRideshareCustomersByValue}
              icon={Users}
              type="customer"
              metric="totalValue"
              isHighest={true}
            />
          </div>
        </div>

        {/* Lowest Performers */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <TrendingDown className="mr-2 text-red-500" />
            Lowest Performers
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <PerformanceTable
              title="Bottom 20 Drivers by Trip Count"
              data={bottomDriversByCount}
              icon={UserCheck}
              type="driver"
              metric="tripCount"
              isHighest={false}
            />
            <PerformanceTable
              title="Bottom 20 Customers by Trip Count"
              data={bottomCustomersByCount}
              icon={Users}
              type="customer"
              metric="tripCount"
              isHighest={false}
            />
            <PerformanceTable
              title="Bottom 20 Drivers by Trip Value"
              data={bottomDriversByValue}
              icon={UserCheck}
              type="driver"
              metric="totalValue"
              isHighest={false}
            />
            <PerformanceTable
              title="Bottom 20 Customers by Trip Value"
              data={bottomCustomersByValue}
              icon={Users}
              type="customer"
              metric="totalValue"
              isHighest={false}
            />
          </div>
        </div>

        {/* Inactive Performers */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <AlertCircle className="mr-2 text-orange-500" />
            Inactive Performers (Zero Activity)
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <PerformanceTable
              title="Inactive Drivers by Trip Count"
              data={inactiveDriversByCount}
              icon={UserCheck}
              type="driver"
              metric="tripCount"
              isHighest={false}
            />
            <PerformanceTable
              title="Inactive Customers by Trip Count"
              data={inactiveCustomersByCount}
              icon={Users}
              type="customer"
              metric="tripCount"
              isHighest={false}
            />
            <PerformanceTable
              title="Inactive Drivers by Trip Value"
              data={inactiveDriversByValue}
              icon={UserCheck}
              type="driver"
              metric="totalValue"
              isHighest={false}
            />
            <PerformanceTable
              title="Inactive Customers by Trip Value"
              data={inactiveCustomersByValue}
              icon={Users}
              type="customer"
              metric="totalValue"
              isHighest={false}
            />
          </div>
        </div>
      </div>
      </div>
    </DashboardLayout>
  );
};

export default PerformanceReports;
